import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AuthGuard, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { lfxPreset } from '@linuxfoundation/lfx-ui-core';
import { OpenFeatureModule } from '@openfeature/angular-sdk';
import { LaunchDarklyClientProvider } from '@openfeature/launchdarkly-client-provider';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { basicLogger } from 'launchdarkly-js-client-sdk';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

/**
 * Recursively converts lfxPreset design tokens into plain string values that
 * PrimeNG's definePreset() expects. Required because @linuxfoundation/lfx-ui-core
 * exports tokens as objects — { $value, $type } (DTCG, v0.2.0+) or { value, type }
 * (legacy, v0.0.x) — instead of plain strings. Without this, CSS custom properties
 * resolve to [object Object] and all themed component styling breaks (dialog backdrops,
 * overlay colors, etc.). This can be removed once lfx-ui-core exports plain string tokens.
 * Non-string leaf values (e.g. shadow objects) are dropped — Aura base provides defaults.
 */
function flattenTokens(obj: any): any {
  if (obj == null || typeof obj !== 'object') {
    return obj;
  }
  if ('$value' in obj && Object.keys(obj).every((k) => k === '$value' || k === '$type')) {
    return typeof obj.$value === 'string' ? obj.$value : undefined;
  }
  if ('value' in obj && Object.keys(obj).every((k) => k === 'value' || k === 'type')) {
    return typeof obj.value === 'string' ? obj.value : undefined;
  }
  const result: any = {};
  for (const key of Object.keys(obj)) {
    if (key === 'figma') continue;
    const val = flattenTokens(obj[key]);
    if (val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}

const customPreset = definePreset(Aura, {
  primitive: flattenTokens(lfxPreset.primitive),
  semantic: flattenTokens(lfxPreset.semantic),
  components: {
    ...flattenTokens(lfxPreset.component),
    button: {
      ...flattenTokens(lfxPreset.component.button),
      primary: {
        hover: {
          background: 'var(--p-color-primary-500)',
        },
      },
    },
    toggleswitch: {
      ...flattenTokens(lfxPreset.component.toggleswitch),
      handle: {
        ...flattenTokens(lfxPreset.component.toggleswitch?.handle),
        border: {
          radius: '50%',
        },
      },
    },
    table: {},
  },
});

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      CommonModule,
      AppRoutingModule,
      ReactiveFormsModule,
      FormsModule,
      RouterModule,
      AuthModule.forRoot({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        useCookiesForTransactions: true,
        authorizationParams: {
          redirect_uri: environment.auth0.redirectUrl,
          audience: environment.auth0.audience,
          scope: environment.auth0.scope,
        },
        errorPath: '/service-unavailable',
        httpInterceptor: {
          allowedList: [
            {
              uri: `${environment.apiURL}/*`,
              allowAnonymous: true,
            },
            {
              uri: `${environment.apiV2URL}/*`,
              allowAnonymous: true,
            },
          ],
        },
      }),
      OpenFeatureModule.forRoot({
        provider: new LaunchDarklyClientProvider(environment.launchDarklyClientId, {
          initializationTimeout: 5,
          streaming: true,
          logger: basicLogger({ level: 'none' }),
        }),
      }),
      TooltipModule,
      ToastModule
    ),
    AuthGuard,
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: Window, useValue: window },
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: customPreset,
        options: {
          darkModeSelector: '.my-app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
  ],
}).catch((err) => console.error(err));
